import type { ReactNode } from 'react';
import type { Viewing } from '@bbbk/schema';
import { deskCounts } from './today';
import { viewingCountLabel } from './viewings';
import type { Booking, BookingStatus } from '../bookings/types';

interface DeskSectionProps {
  kind: 'arrivals' | 'collections';
  title: string;
  // True only on the live today during the section's window — emphasises the
  // section and its counts (no time-label pill).
  inWindow: boolean;
  bookings: Booking[];
  doneStatuses: BookingStatus[];
  renderRow: (b: Booking) => ReactNode;
  // An optional control in the section head, right-aligned — Collections carries
  // the staff message there, in the same position it held in the On-duty card
  // head before the strip replaced that card.
  action?: ReactNode;
  // VIEWINGS SIT AMONG THE ROWS BUT OUTSIDE THE COUNT (viewing-on-the-desk AC1,
  // AC8). They are interleaved here rather than by the caller so ONE sort
  // decides the order of everything on the desk — two lists concatenated would
  // put every viewing above or below the dogs, and "among the dogs arriving" is
  // the criterion.
  viewings?: Viewing[];
  renderViewing?: (v: Viewing) => ReactNode;
  // THE VIEWINGS READ HAS NOT ANSWERED — never landed, failed, or is still for
  // the previous window. An empty list and an unanswered question are the same
  // value and must never be the same message: without this the desk shows no
  // viewing rows and, on a day with no arrivals either, says "No drop-offs
  // scheduled" — a claim about the day made from a request that said nothing.
  // Somebody could miss an expected visitor on the strength of it.
  // The desk's own rows are unaffected; only the viewings are unknown.
  viewingsUnknown?: boolean;
}

// A desk section — drop-offs or collections for the day. Header carries the title
// and an expected/done/to-go count, emphasised while the section is live in its
// window. Rows sort alphabetically by primary dog name so they hold their place
// when a status flips. Empty days show a clear "nothing on" line, not a blank panel.
export function DeskSection({
  kind,
  title,
  inWindow,
  bookings,
  doneStatuses,
  renderRow,
  action,
  viewings = [],
  renderViewing,
  viewingsUnknown = false,
}: DeskSectionProps) {
  const sorted = [...bookings].sort((a, b) =>
    (a.dogs[0]?.name ?? '').toLowerCase().localeCompare((b.dogs[0]?.name ?? '').toLowerCase()),
  );
  // THE COUNT IS OVER THE BOOKINGS ALONE. "1/2 done" means dogs arriving, and a
  // viewing is not a dog — counting one inside it would silently change what a
  // figure Sir already reads means.
  const { expected, done, toGo } = deskCounts(sorted, doneStatuses);
  // Say nothing about a number that was never read.
  const viewingLabel = renderViewing && !viewingsUnknown ? viewingCountLabel(viewings.length) : null;

  // One list, one sort. A row's key is the name it leads with — the first dog
  // for a booking, the contact for a viewing — so a viewing lands where its name
  // puts it rather than at an end.
  const rows: { key: string; sortKey: string; node: ReactNode }[] = [
    ...sorted.map((b) => ({
      key: 'b' + b.id,
      sortKey: (b.dogs[0]?.name ?? '').toLowerCase(),
      node: renderRow(b),
    })),
    ...(renderViewing
      ? viewings.map((v) => ({
          key: 'v' + v.id,
          // SORTED BY WHAT THE ROW SHOWS FIRST, which is now the dogs. A booking
          // sorts by its first dog's name; a viewing sorting by its CONTACT put
          // Zoe-with-Alfie down among the Zs while its visible lead column read
          // "Alfie", and the column stopped being alphabetical (codex, PR #172).
          // The contact is the fallback for a viewing that mentioned no dog,
          // which is the only case where the contact IS the lead.
          sortKey: (v.dogs.find((d) => d.name)?.name ?? v.contactName).toLowerCase(),
          node: renderViewing(v),
        }))
      : []),
  ].sort((a, b) => a.sortKey.localeCompare(b.sortKey));

  return (
    <div className={'today-section desk-section' + (inWindow ? ' in-window' : '')}>
      <div className="desk-section-head">
        <div className="desk-section-title">
          <h3>{title}</h3>
          <span className={'desk-counts' + (toGo > 0 && inWindow ? ' active' : '')}>
            <span className="mono">
              {done}/{expected}
            </span>{' '}
            done
          </span>
          {/* MENTIONED BESIDE THE COUNT, NEVER INSIDE IT (AC8). Without it the
              count silently excludes a row the reader can plainly see. */}
          {viewingLabel && <span className="desk-counts-viewings">· {viewingLabel}</span>}
          {renderViewing && viewingsUnknown && (
            <span className="desk-counts-unknown" title="The viewings for this day have not been read. The dogs arriving are unaffected.">
              · viewings not read
            </span>
          )}
        </div>
        {action}
      </div>

      {/* The nodes are rendered BARE, not wrapped. `.desk-row` is a direct child
          of `.desk-list` in every rule that styles this desk, and a wrapper
          element for the sake of a key would change the DOM shape the stylesheet
          is written against. Each renderer supplies its own key. */}
      {rows.length === 0 ? (
        <div className="empty-state">
          No {kind === 'arrivals' ? 'drop-offs' : 'collections'} scheduled.
          {renderViewing && viewingsUnknown && ' Any viewings have not been read.'}
        </div>
      ) : (
        <div className="desk-list">{rows.map((r) => r.node)}</div>
      )}
    </div>
  );
}
